import React, { useContext, useEffect, useState } from "react";

import styled from "styled-components/macro";

import { FlocknoteAppState } from "Flocknote/state";

import API from "Signups/API.js";
import FamilyRegistration from "Signups/FamilyRegistration";
import API2 from "Signups/SignupView/API";
import { SignupsAppState } from "Signups/state.js";

import FnrLoadingBricks from "UI/FnrLoadingBricks";

import FetchSignupInfoError from "./FetchSignupInfoError";
import NewSignup, { useNewSignupCreationFlow } from "./NewSignup";
import SignupSheet from "./SignupSheet";
import "./SignupView.scss";

export default function SignupView({ signupListID, action }) {
  const [appState] = useContext(FlocknoteAppState);
  const [state, dispatch] = useContext(SignupsAppState);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [loadingVerbs, setLoadingVerbs] = useState(false);
  const { initialDataLoaded } = state; // if the signup lists data is loaded

  const { cancelCreationFlow, forceToAddItem } = useNewSignupCreationFlow();

  useEffect(() => {
    if (action) cancelCreationFlow();
  }, [action, cancelCreationFlow]);
  useEffect(() => {
    if (!forceToAddItem) cancelCreationFlow();
  }, []);

  useEffect(() => {
    setFetchFailed(false);
    dispatch({ type: "Remove Current Signup List" });
    // console.log({ signupListID, asInt: parseInt(signupListID) })
    if (!signupListID || parseInt(signupListID) === 0) return;
    const getSignupListInfo = API2.getSignupListInfo(signupListID);

    setIsLoading(true);
    getSignupListInfo
      .post()
      .then((signupListInfo) => {
        dispatch({
          type: "Set Current Signup List",
          ...signupListInfo,
        });

        // Update the available slots in the ListsHolder component
        const numConfirmedSignups =
          signupListInfo.signupList.signupItems.reduce((total, item) => {
            return total + (item.numberOfRespondents || 0);
          }, 0);
        dispatch({
          type: "Update Confirmed Signups",
          ID: signupListID,
          value: numConfirmedSignups,
        });

        const listIsArchived =
          signupListInfo.signupList?.signupListStatusID === 4;

        if (listIsArchived) {
          // if current list is archived, make sure the lists holder is set to showing archived
          dispatch({ type: "setShowArchivedSignups", payload: true });
        }
      })
      .catch(() => {
        setFetchFailed(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
    return () => getSignupListInfo.cancel();
  }, [signupListID, dispatch]);

  useEffect(() => {
    if (
      (action === "new" || action === "family-registration") &&
      !state.editingSignupList
    ) {
      dispatch({ type: "Create New Signup" });
    }
    // eslint-disable-next-line
  }, [action, state.editingSignupList, dispatch]);

  useEffect(() => {
    if (
      loadingVerbs ||
      action !== "new" ||
      !state.editingSignupList ||
      state.defaultVerbs
    )
      return;

    setLoadingVerbs(true);

    const getDefaultSignupVerbs = API.getDefaultSignupListTypeVerb();

    getDefaultSignupVerbs.post().then((resp) => {
      dispatch({ type: "Set Default Verbs", verbs: resp.verbs });
      setLoadingVerbs(false);
    });

    return () => getDefaultSignupVerbs.cancel();
    // eslint-disable-next-line
  }, [action, state]);

  if (!state.currentSignupList && !state.editingSignupList && fetchFailed) {
    return <FetchSignupInfoError />;
  }

  const renderOffsetSpacer = () => {
    // this is a helper to visually center the new signup creation form
    try {
      if (action === "new" && appState.composer?.minimized) {
        // when being viewed with a minimized composer toolbar
        return <MinimizedComposerSpacer />;
      } else if (action === "new" && !state.allSignupLists.length) {
        // when there are no other signup lists
        return <EmptyListHolderSpacer />;
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  };

  return (
    <Wrapper className="SignupView" data-cy="SignupView_Wrapper">
      {isLoading || !initialDataLoaded ? (
        <Loading>
          <FnrLoadingBricks show size={52} />
        </Loading>
      ) : (
        <>
          {action === "new" ? (
            <NewSignup />
          ) : action === "family-registration" ? (
            <FamilyRegistration />
          ) : (
            <>
              <SignupSheet />
              {renderOffsetSpacer()}
            </>
          )}
        </>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
`;

const OffsetSpacer = styled.div`
  width: 340px;
  flex-shrink: 0;
`;

const MinimizedComposerSpacer = styled(OffsetSpacer)`
  @media (max-width: 1140px) {
    display: none;
  }
`;
const EmptyListHolderSpacer = styled(OffsetSpacer)`
  @media (max-width: 1480px) {
    display: none;
  }
`;

const Loading = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
